import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAlert } from "react-alert";
import {
  UserContext,
  SettingsContext,
  FeatureGatingContext,
} from "../../../contexts";
import { Collapsible } from "../../../components";
import { ConfirmRequest as ConfirmRequestComp } from "../../../product/components";
import { useRequestContext } from "../../hooks/useRequest";
import { dataToForm } from "../../helpers/request";
import { journeyConst } from "../../constants";
import { createBuyAlertWatch } from "../../helpers/buyAlertWatch.js";
import { useActiveStoreIdM } from "../../../core/hooks/useActiveStoreIdM.js";

// Checkmark icon — confirms the request step.
const CheckIcon = () => (
  <svg
    className="rc-lp__section-icon"
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden
  >
    <path d="M3 8.5L6.5 12L13 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Exported for tests: the lock rules are load-bearing (they decide when a
// deal can grow retroactively) and the component needs the full request
// context to render, so the derivation is pinned at the function.
export const setupButtons = (
  requestType,
  pxProducts,
  isEditing,
  pxAllowed,
  mainCount,
  requestHasOrder,
  requestGroupId,
  isAdditionalMainSlot,
  flowVariant
) => {
  // A purpose-built flow renames this row and can add a fourth button. The
  // `option` values are unchanged, so the handlers behind them are the existing
  // ones — only the wording and the new slot's default tab differ.
  const labels = flowVariant?.confirmLabels;

  if (requestType === "sell" || requestType === "stock" || !pxAllowed) {
    // The sell-side variants still get their own wording for the single
    // validate/place button.
    return [
      {
        label: labels?.finish ?? "Validate",
        option: "finish",
      },
    ];
  }

  // Every CONFIRM REQUEST slot (primary OR additional main) gets the
  // full 3-button row. The rep should be able to add another product
  // or add part-exchange from any slot — the caps below gate the
  // disabled state, not the visibility.
  // Caps + guards:
  //   3 PX products max  → "Add part exchange" disables at 3
  //   3 main products max → "Add another product" disables at 3
  //
  // Retroactive adds (the EDIT flow, where request.order_id already exists)
  // are allowed since 2026-08-19 — the Day-1 lock from PR #108 predates
  // request groups. They are safe BECAUSE of the group: a new sibling is
  // created with the primary's group_id, and the backend resolves any group
  // member's listing to the deal's ONE order (existingOrderForCart walks
  // group_id → order_id), so the new item lands on the existing order and
  // PAYMENTS / the summary card follow. A legacy GROUP-LESS request stays
  // locked: its new sibling could not make that resolution and would mint a
  // second order for the same deal — and PX sell drafts only rehydrate from
  // the group, so on reload they would silently disappear.
  const retroLocked = !!requestHasOrder && !requestGroupId;
  const pxLocked =
    pxProducts.length > 2 || (retroLocked && requestType === "order");
  const mainLocked = mainCount >= 3 || retroLocked;

  const buttons = [
    {
      label: labels?.add_main ?? "Add another product",
      option: "add_main",
      disabled: mainLocked,
      // Which SEARCH tab the slot this creates should open on. Undefined on
      // the stock flow → V2SearchProduct keeps its existing default.
      newSlotSource: labels?.add_main ? flowVariant?.searchDefault : undefined,
    },
    {
      label:
        labels?.add ??
        (!pxProducts.length ? "Add part exchange" : "Add more products for sell"),
      option: "add",
      disabled: pxLocked,
    },
    {
      label:
        labels?.finish ??
        (!pxProducts.length ? "Continue without" : "Finalize part exchange"),
      option: "finish",
    },
  ];

  // The optional fourth button: a SECOND add_main whose new slot opens on a
  // different tab. Record a Sale uses it for "Source Product" (Database) next
  // to "Add Listing" (Active Listings) — same handler, same cap, one differing
  // default. Inserted after its sibling so the two read as a pair.
  if (labels?.addMainAs) {
    buttons.splice(1, 0, {
      label: labels.addMainAs.label,
      option: "add_main",
      disabled: mainLocked,
      newSlotSource: labels.addMainAs.searchDefault,
    });
  }

  return buttons;
};

export function ConfirmRequest({ step, idx, onAdvance, navigateTo }) {
  const { user } = useContext(UserContext);
  const { isMerchant } = useContext(SettingsContext);
  const [pxAllowed, setPxAllowed] = useState(
    !isMerchant || user?.is_merchant_admin
  );
  const { checkFeatureQuota } = useContext(FeatureGatingContext);
  const {
    getContext,
    confirmRequest,
    toggleStep,
    client,
    requestType,
    pxProducts,
    paidOffline,
    flowVariant,
  } = useRequestContext();

  // Pass the step down so getContext honors step.pxSellIdx — without
  // this, dynamic Confirm Sell Request (m/n) sections would read the
  // main request's product/data instead of the px sell-request's.
  const { product, request, data, isEditing, isSell } = getContext(idx, step);
  const { additionalMainRequests = [] } = useRequestContext();

  // The store the rep is acting as — the Sales Pipeline watch belongs to it,
  // not to whichever store the user happens to live in.
  const storeIdM = useActiveStoreIdM();
  const alert = useAlert();

  // mainSlotIdx > 0 → rep is on the CONFIRM REQUEST for the 2nd or
  // 3rd main product. Show a slimmer button row (Save + Continue).
  const isAdditionalMainSlot =
    step?.mainSlotIdx != null && step.mainSlotIdx > 0;

  // mainCount = primary (always 1 once we're on this section) + any
  // additional mains the rep has already added. Used to disable
  // "Add another product" once we hit the 3-cap.
  const mainCount = 1 + (additionalMainRequests?.length ?? 0);

  const buttons = useMemo(
    () =>
      setupButtons(
        requestType,
        pxProducts,
        isEditing,
        pxAllowed,
        mainCount,
        request?.order_id,
        request?.group_id,
        isAdditionalMainSlot,
        flowVariant
      ),
    [
      requestType,
      pxProducts,
      isEditing,
      pxAllowed,
      mainCount,
      request?.order_id,
      request?.group_id,
      isAdditionalMainSlot,
      flowVariant,
    ]
  );

  const checkQuota = async () => {
    const allowed = await checkFeatureQuota(
      FeatureGatingContext.features.PART_EXCHANGE
    );

    setPxAllowed(allowed);
  };

  useEffect(() => {
    if (!isMerchant || user?.is_merchant_admin) {
      return;
    }

    checkQuota();
  }, [isMerchant, user?.is_merchant_admin]);

  // For ADD / ADD_MAIN: confirmRequest mutates the journey to insert a new
  // SEARCH section and returns a { nextSectionId } hint pointing at it. Using
  // the returned id (computed from fresh state inside the hook) avoids the
  // closure-staleness bug where reading `journey` here saw the pre-mutation
  // list and computed a different slot id than the one that was actually
  // emitted — which caused additional-slot button clicks (slot 2, slot 3) to
  // land on nothing.
  const submit = async (data, action) => {
    const result = await confirmRequest(step, idx, data, action);

    // The buy-alert watch is created AFTER the request is safely confirmed,
    // and its failure never throws into the flow. A watch is a convenience; the
    // deal is the transaction. Letting a 500 here throw would leave the rep
    // staring at an error on a request that actually saved, and they would
    // reasonably try again — which is how a duplicate order gets placed.
    //
    // It is no longer SILENT, though: a toast names the failure. Swallowing it
    // is how three sourcing requests went missing from the Sales Pipeline on
    // 2026-10-13 with nobody told.
    if (data?.watchSupplier) {
      createBuyAlertWatch({
        productId: product?.id,
        storeIdM,
        data,
        requestId: request?.id,
      }).catch((err) => {
        const reason =
          err?.response?.data?.message || err?.message || "unknown error";
        alert?.error?.(
          `Saved, but the Sales Pipeline watch failed — ${reason}`,
          { timeout: 10000 }
        );
      });
    }

    if (result?.nextSectionId && navigateTo) {
      navigateTo(result.nextSectionId);
      return;
    }
    onAdvance?.(action);
  };

  // ── Record Sale: finalise without asking ──────────────────────────────────
  // A sale recorded FROM a listing has nothing left to confirm — the listing
  // already carries the year, condition, price and box, and the arrival link
  // has persisted all four onto the request. Making the rep re-read their own
  // listing and press a button is ceremony, so this submits FINISH for them
  // and the existing nav hint carries the flow on to PAYMENTS.
  //
  // Deliberately narrow. It fires only when:
  //   - this is the record-sale variant on the PRIMARY main slot,
  //   - the request is linked to a listing (so the values really are prefilled),
  //   - no order exists yet — an already-finalised deal must never be re-POSTed,
  //   - a client is assigned, which is what gates the arrival link too.
  // Every other flow, and every additional / PX slot, keeps its manual confirm.
  const autoFinalisedRef = useRef(false);
  useEffect(() => {
    if (autoFinalisedRef.current) return;
    if (flowVariant?.key !== "record_sale") return;
    if (step?.pxSellIdx != null || isAdditionalMainSlot) return;
    if (!client?.id) return;
    if (!request?.id || !request?.linked_listing_id) return;
    if (request?.order_id) return;
    autoFinalisedRef.current = true;
    // Sent as the form would send it — camelCase keys, mapped back to columns
    // by saveRequest. `images` stays empty: the request was just created from a
    // listing, so there are none to preserve, and passing the row's own image
    // records back would not survive the round trip.
    submit(
      {
        minBudget: request.min_budget,
        maxBudget: request.max_budget,
        priceExpectation: request.price_expectation,
        year: request.year,
        condition: request.condition,
        deliverySet: request.delivery_set,
        channel: request.channel,
        currency: request.currency,
        images: [],
      },
      journeyConst.actions.FINISH
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    flowVariant?.key,
    client?.id,
    request?.id,
    request?.linked_listing_id,
    request?.order_id,
  ]);

  const trigger = (
    <span className="search-product-trigger-label">
      <CheckIcon />
      <span>{step.label}</span>
    </span>
  );

  return (
    <Collapsible
      trigger={trigger}
      open={step.open}
      handleTriggerClick={() => toggleStep(idx)}
      testId="confirm-request"
    >
      <div className="mt-4">
        <ConfirmRequestComp
          {...{
            productId: product?.id,
            request: request || dataToForm(data, isSell) || {},
            clientName: client
              ? `${client?.first_name} ${client?.last_name}`
              : "",
            onRequestSubmit: submit,
            isEditing,
            requestType,
            buttons,
            paidOffline,
          }}
        />
      </div>
    </Collapsible>
  );
}
